// Connect package 4 (20 Oct 2026; docs/specs/CONNECT.md section 18; migration 20270112090000; step E13): Connect ›
// Rules › Approvals, per campaign and per step of its path. Craig: "per campaign, what things are automatically enabled
// by the agent and which things we need to approve manually still ... each step of its pathway".
// - The board (crm_connect_approvals_board) is raw: the guardrails, the business's level per kind, the campaigns with
//   their path's steps, every campaign setting and open proposals. cellFor works out each cell in the same order as the
//   database's lookup (crm__autonomy_scope_level): this campaign's step, this campaign, the business, the default.
// - STEP_ITEMS says what Ogma may send at each step, in plain words; deal messages are shown locked (always need you).
// - Changes go through act (set_autonomy_scope, decide_autonomy_scope), people only.
import type { Crm } from "./crm";
import type { BookingAutonomy } from "./paths";
import { BLOCKS, CAMPAIGN_MODULES, CAMPAIGN_MODULE_LABEL, type BlockKey, type CampaignModule } from "./paths";

export type Level = "approve_all" | "sample" | "auto";
/** A kind a campaign can set per step (crm__autonomy_scope_kinds), or deal_email, shown locked. */
export type ScopeKind = "interested_reply" | "reply_answer" | "whatsapp_reply" | "meeting_email" | "connect_offer" | "quote";
export type ItemKind = ScopeKind | "deal_email";

export const SCOPE_KINDS: ScopeKind[] = ["interested_reply", "reply_answer", "whatsapp_reply", "meeting_email", "connect_offer", "quote"];

export interface ScopeRow {
  kind: ScopeKind;
  play_id: string;
  /** "" for every step of the campaign. */
  step: string;
  level: Level;
  changed_at?: string;
  changed_by?: string;
  reason?: string;
}

export interface ApprovalsCampaign {
  id: string;
  name: string;
  module: CampaignModule;
  status: string;
  path_key?: string;
  path_label?: string;
  /** How the path was found: the campaign's own (campaign), its module's flavour, or the default. */
  path_from?: "campaign" | "flavour" | "offer" | "default";
  /** "reply" then the path's blocks in force. */
  steps: string[];
  firms?: number;
}

export interface ScopeProposal {
  task_id: string;
  kind: ScopeKind;
  play_id: string;
  step: string;
  from: Level;
  to: Level;
  title?: string;
}

export interface ApprovalsBoard {
  kill: boolean;
  gate_4_10: boolean;
  gate_9_17: boolean;
  quote_check_first: number;
  /** The share of a sample that waits for a person (autonomy.sample_share, 0.01 to 1). */
  sample_share?: number;
  whatsapp_reply_mode: "draft" | "sample" | "auto" | string;
  kinds: ScopeKind[];
  defaults: { kind: ScopeKind; label: string; level: Level; changed_at?: string; changed_by?: string; reason?: string }[];
  scopes: ScopeRow[];
  find_approvals?: Record<string, Record<string, string>>;
  proposals: ScopeProposal[];
  campaigns: ApprovalsCampaign[];
  booking_autonomy: BookingAutonomy;
}

export const LEVEL_WORD: Record<Level, string> = { approve_all: "You approve each one", sample: "You approve a sample", auto: "Automatic" };
export const LEVEL_SHORT: Record<Level, string> = { approve_all: "Approve", sample: "Sample", auto: "Automatic" };

export const KIND_LABEL: Record<ItemKind, string> = {
  interested_reply: "Replies to interested people",
  reply_answer: "Answers to questions",
  whatsapp_reply: "WhatsApp replies",
  meeting_email: "Booking and meeting emails",
  connect_offer: "Save offers",
  quote: "Quotes",
  deal_email: "Deal messages",
};

export interface StepItem {
  kind: ItemKind;
  /** What Ogma does, as a verb phrase: "send the booking email". */
  covers: string;
}

const DEAL = (covers: string): StepItem => ({ kind: "deal_email", covers });
const ANSWERS: StepItem[] = [
  { kind: "reply_answer", covers: "answer their questions" },
  { kind: "whatsapp_reply", covers: "reply on WhatsApp" },
];

/** What Ogma may send at each step (keep in step with docs/specs/CONNECT.md 18.2). A block not listed sends deal messages only. */
export const STEP_ITEMS: Record<string, StepItem[]> = {
  "": [
    { kind: "interested_reply", covers: "reply to people who say they're interested" },
    ...ANSWERS,
    { kind: "meeting_email", covers: "send booking and meeting emails" },
    { kind: "connect_offer", covers: "send the save offer" },
    { kind: "quote", covers: "send the quote" },
  ],
  reply: [
    { kind: "interested_reply", covers: "reply to people who say they're interested" },
    ...ANSWERS,
    { kind: "meeting_email", covers: "send the booking email" },
  ],
  qualify: ANSWERS,
  gather: ANSWERS,
  book: [{ kind: "meeting_email", covers: "send the booking email" }, { kind: "whatsapp_reply", covers: "reply on WhatsApp" }],
  meet: [{ kind: "meeting_email", covers: "send the reminders, the pre-read and a new time after a no-show" }],
  recap: [DEAL("send the meeting follow-up (the recap)")],
  proposal: [DEAL("send the proposal")],
  quote: [{ kind: "quote", covers: "send the quote" }, DEAL("chase a quote they have opened")],
  follow_up: [{ kind: "connect_offer", covers: "send the save offer" }, DEAL("chase them for a decision")],
};

export function stepItems(step: string): StepItem[] {
  return STEP_ITEMS[step] ?? [DEAL("send messages about the deal")];
}

export function stepName(step: string): string {
  if (step === "") return "Every step";
  if (step === "reply") return "First reply";
  return BLOCKS[step as BlockKey]?.label ?? step;
}

export interface Cell {
  /** What happens now. */
  level: Level;
  /** Where it comes from. */
  from: "locked" | "step" | "campaign" | "business";
  /** The campaign's own setting at exactly this step ("" row: the campaign's every-step setting). */
  own: ScopeRow | null;
  /** What it would be without its own setting, in words: "Business default: approve each one". */
  inherits: string;
  /** Always needs a person, and why (the cell cannot be changed). */
  locked: string | null;
  /** Shown under the cell: a cap or a rule that still applies. */
  note: string | null;
  /** The levels a person may choose here (empty when locked). */
  choices: Level[];
  proposal: ScopeProposal | null;
}

const RANK: Record<Level, number> = { approve_all: 0, sample: 1, auto: 2 };
const BY_RANK: Level[] = ["approve_all", "sample", "auto"];

function scopeOf(b: ApprovalsBoard, kind: string, play: string, step: string): ScopeRow | null {
  return b.scopes.find((s) => s.kind === kind && s.play_id === play && s.step === step) ?? null;
}

/** The business's own level for a kind, with words for it. */
export function businessDefault(b: ApprovalsBoard, kind: ScopeKind): { level: Level; text: string } {
  if (kind === "quote") {
    if (b.gate_9_17) return { level: "approve_all", text: "you check every quote (9.17 is one of your approval gates)" };
    return { level: "auto", text: `automatic after the first ${b.quote_check_first} quotes of each price list` };
  }
  const lv = (b.defaults.find((d) => d.kind === kind)?.level ?? "approve_all") as Level;
  if (kind === "whatsapp_reply") {
    // the lower of the reply mode and the level earned (with no 4.10 gate, the reply mode alone)
    const mode = modeLevel(b.whatsapp_reply_mode);
    const capped = b.gate_4_10 ? BY_RANK[Math.min(RANK[lv], RANK[mode])]! : mode;
    return { level: capped, text: `${LEVEL_WORD[capped].toLowerCase()} (WhatsApp reply mode: ${b.whatsapp_reply_mode})` };
  }
  if (!b.gate_4_10) return { level: "auto", text: "goes without approval (4.10 is not one of your approval gates)" };
  if (kind === "meeting_email") return { level: lv, text: `booking messages switch ${lv === "auto" ? "on: automatic" : "off: approve each one"}` };
  return { level: lv, text: LEVEL_WORD[lv].toLowerCase() };
}

function modeLevel(mode: string): Level {
  return mode === "auto" ? "auto" : mode === "sample" ? "sample" : "approve_all";
}

/**
 * One cell: a kind at one step of one campaign ("" = the campaign's every-step row). Same order as the database:
 * the step's own setting, the campaign's, the business's; the guardrails first.
 */
export function cellFor(b: ApprovalsBoard, campaign: ApprovalsCampaign, step: string, kind: ItemKind): Cell {
  const base = { own: null, note: null, choices: [] as Level[], proposal: null };
  if (kind === "deal_email") {
    return { ...base, level: "approve_all", from: "locked", inherits: "", locked: "Deal messages always need your approval." };
  }
  if (b.kill) {
    return { ...base, level: "approve_all", from: "locked", inherits: "", locked: "The kill switch is on: everything waits for you." };
  }
  if (kind === "quote" && b.gate_9_17) {
    return { ...base, level: "approve_all", from: "locked", inherits: "", locked: "You check every quote: 9.17 is one of your approval gates." };
  }
  // A retired campaign never sends by itself: its sample or automatic is ignored, its approve each one still holds
  // (crm__autonomy_scope_level).
  const retired = campaign.status === "retired";
  const counts = (r: ScopeRow | null) => (r && (r.level === "approve_all" || !retired) ? r : null);
  const own = scopeOf(b, kind, campaign.id, step);
  const wideRow = step === "" ? null : scopeOf(b, kind, campaign.id, "");
  const wide = counts(wideRow);
  const biz = businessDefault(b, kind);
  const inherits = wide ? `This campaign, every step: ${LEVEL_WORD[wide.level].toLowerCase()}` : `Business default: ${biz.text}`;
  const inheritedLevel = wide ? wide.level : biz.level;
  let level: Level = counts(own) ? own!.level : inheritedLevel;
  let note: string | null = retired && ((own && !counts(own)) || (wideRow && !wide)) ? "Retired: it never sends anything by itself; clear this setting." : null;
  if (kind === "whatsapp_reply" && (own || wide)) {
    const cap = modeLevel(b.whatsapp_reply_mode);
    if (RANK[level] > RANK[cap]) {
      level = cap;
      note = `Held back by your WhatsApp reply mode (${b.whatsapp_reply_mode}).`;
    }
  }
  if (kind === "quote" && !note) {
    note = `Quotes still wait for you while they are among the first ${b.quote_check_first} of a price list, or when a rule refers them to you.`;
  } else if (!note && !b.gate_4_10 && level !== "approve_all") {
    note = level === "sample" ? "A sample waits for you; the rest go." : null;
  }
  const from: Cell["from"] = own ? (step === "" ? "campaign" : "step") : wide ? "campaign" : "business";
  const choices: Level[] = kind === "quote" || retired ? ["approve_all"] : ["approve_all", "sample", "auto"];
  const proposal = b.proposals.find((p) => p.kind === kind && p.play_id === campaign.id && p.step === step) ?? null;
  return { level, from, own, inherits, locked: null, note, choices, proposal };
}

/** The campaigns grouped by module, in module order, each group in the board's order. */
export function campaignsByModule(list: ApprovalsCampaign[]): { module: CampaignModule; label: string; campaigns: ApprovalsCampaign[] }[] {
  return CAMPAIGN_MODULES.map((m) => ({ module: m, label: CAMPAIGN_MODULE_LABEL[m], campaigns: list.filter((c) => c.module === m) })).filter((g) => g.campaigns.length > 0);
}

/** A campaign's card line: "3 automatic, 4 wait for you, 2 always need you". */
export function campaignSummary(b: ApprovalsBoard, c: ApprovalsCampaign): string {
  let auto = 0;
  let wait = 0;
  let locked = 0;
  for (const step of c.steps) {
    for (const it of stepItems(step)) {
      const cell = cellFor(b, c, step, it.kind);
      if (cell.locked) locked += 1;
      else if (cell.level === "auto") auto += 1;
      else wait += 1;
    }
  }
  const parts = [`${auto} automatic`, `${wait} ${wait === 1 ? "waits" : "wait"} for you${wait ? " (all or a sample)" : ""}`];
  if (locked) parts.push(`${locked} always ${locked === 1 ? "needs" : "need"} you`);
  return parts.join(", ");
}

/** How many settings this campaign has of its own. */
export function ownSettings(b: ApprovalsBoard, c: ApprovalsCampaign): number {
  return b.scopes.filter((s) => s.play_id === c.id).length;
}

// ---------------------------------------------------------------- calls
export function approvalsBoard(crm: Crm): Promise<ApprovalsBoard> {
  return crm.read<ApprovalsBoard>("crm_connect_approvals_board");
}

/** One campaign's level for a kind at a step ("" = every step); null follows the business again. People only. */
export function setScope(crm: Crm, kind: ScopeKind, campaign: string, step: string, level: Level | null, reason?: string) {
  return crm.write<{ changed: boolean; level: Level | null; pulled_back?: number }>("set_autonomy_scope", {
    kind, campaign, step, level, ...(reason ? { reason } : {}),
  });
}

export function decideScope(crm: Crm, task: string, accept: boolean, note?: string) {
  return crm.write<{ status: "promoted" | "withdrawn" | "declined"; level?: Level; blockers?: string[] }>("decide_autonomy_scope", {
    task, accept, ...(note ? { note } : {}),
  });
}

/** "about 1 in 10": the sample share in words. */
export function sampleWords(share: number | null | undefined): string {
  const n = Number(share ?? 0.1);
  if (!Number.isFinite(n) || n <= 0) return "about 1 in 10";
  if (n >= 1) return "every one";
  return `about 1 in ${Math.max(2, Math.round(1 / n))}`;
}

/**
 * The campaign settings that still send by themselves (sample or automatic) on campaigns that are not retired, for
 * the kinds given (all when none): after a person moves a kind back for the business, these keep going until changed
 * on Settings › Approvals and spend › Conversation approvals (a person's demotion leaves campaign settings alone).
 */
export function looseSettings(b: ApprovalsBoard, kinds?: ScopeKind[]): { row: ScopeRow; campaign: string }[] {
  const byId = new Map(b.campaigns.map((c) => [c.id, c]));
  return b.scopes
    .filter((s) => s.level !== "approve_all" && (!kinds || kinds.includes(s.kind)))
    .map((row) => ({ row, c: byId.get(row.play_id) }))
    .filter((x) => x.c && x.c.status !== "retired")
    .map((x) => ({ row: x.row, campaign: x.c!.name }));
}
